import EventEmitter from "events";
import { SimpleObservable } from "matrix-widget-api";
import { logger as baseLogger } from "matrix-js-sdk/src/logger";

import { UPDATE_EVENT } from "../stores/AsyncStore";
import { arrayFastResample } from "../utils/arrays";
import { type IDestroyable } from "../utils/IDestroyable";
import { PlaybackClock } from "./PlaybackClock";
import { cloneArrayBuffer, createAudioContext, decodeOgg } from "./compat";
import { clamp } from "../shared-components/utils/numbers";
import { DEFAULT_WAVEFORM, PLAYBACK_WAVEFORM_SAMPLES } from "./consts";
import { PlaybackEncoder } from "../PlaybackEncoder";

const logger = baseLogger.getChild("Playback");

export enum PlaybackState {
    Decoding = "decoding",
    Stopped = "stopped", // no progress on timeline
    Paused = "paused", // some progress on timeline
    Playing = "playing", // active progress through timeline
}

const THUMBNAIL_WAVEFORM_SAMPLES = 100; // arbitrary: [30,120]

export interface PlaybackInterface {
    readonly currentState: PlaybackState;
    readonly liveData: SimpleObservable<number[]>;
    readonly timeSeconds: number;
    readonly durationSeconds: number;
    skipTo(timeSeconds: number): Promise<void>;
}

export class Playback extends EventEmitter implements IDestroyable, PlaybackInterface {
    /**
     * Stable waveform for representing a thumbnail of the media. Values are
     * guaranteed to be between zero and one, inclusive.
     */
    public readonly thumbnailWaveform: number[];

    private readonly context: AudioContext;
    private source?: MediaElementAudioSourceNode;
    private state = PlaybackState.Decoding;
    private audioBuf?: AudioBuffer;
    private element?: HTMLAudioElement;
    private resampledWaveform: number[];
    private waveformObservable = new SimpleObservable<number[]>();
    private readonly clock: PlaybackClock;
    private readonly fileSize: number;
    private _playbackRate: number = 1;

    /**
     * Creates a new playback instance from a buffer.
     * @param {ArrayBuffer} buf The buffer containing the sound sample.
     * @param {number[]} seedWaveform Optional seed waveform to present until the proper waveform
     * can be calculated. Contains values between zero and one, inclusive.
     */
    public constructor(
        private buf: ArrayBuffer,
        seedWaveform = DEFAULT_WAVEFORM,
    ) {
        super();
        // Capture the file size early as reading the buffer will result in a 0-length buffer left behind
        this.fileSize = this.buf.byteLength;
        this.context = createAudioContext();
        this.resampledWaveform = arrayFastResample(seedWaveform ?? DEFAULT_WAVEFORM, PLAYBACK_WAVEFORM_SAMPLES);
        this.thumbnailWaveform = arrayFastResample(seedWaveform ?? DEFAULT_WAVEFORM, THUMBNAIL_WAVEFORM_SAMPLES);
        this.waveformObservable.update(this.resampledWaveform);
        this.clock = new PlaybackClock(this.context);
    }

    /**
     * Current playback speed multiplier.
     */
    public get playbackRate(): number {
        return this._playbackRate;
    }

    /**
     * Set playback speed (1, 1.5, 2, etc.). Can be called during playback;
     * the change takes effect immediately with pitch preserved.
     */
    public setPlaybackRate(rate: number): void {
        this._playbackRate = rate;
        if (this.element) {
            this.element.playbackRate = rate;
            this.element.preservesPitch = true;
        }
    }

    /**
     * Size of the audio clip in bytes. May be zero if unknown. This is updated
     * when the playback goes through phase changes.
     */
    public get sizeBytes(): number {
        return this.fileSize;
    }

    /**
     * Stable waveform for the playback. Values are guaranteed to be between
     * zero and one, inclusive.
     */
    public get waveform(): number[] {
        return this.resampledWaveform;
    }

    public get waveformData(): SimpleObservable<number[]> {
        return this.waveformObservable;
    }

    public get clockInfo(): PlaybackClock {
        return this.clock;
    }

    public get liveData(): SimpleObservable<number[]> {
        return this.clock.liveData;
    }

    public get timeSeconds(): number {
        return this.clock.timeSeconds;
    }

    public get durationSeconds(): number {
        return this.clock.durationSeconds;
    }

    public get currentState(): PlaybackState {
        return this.state;
    }

    public get isPlaying(): boolean {
        return this.currentState === PlaybackState.Playing;
    }

    public emit(event: PlaybackState, ...args: any[]): boolean {
        this.state = event;
        super.emit(event, ...args);
        super.emit(UPDATE_EVENT, event, ...args);
        return true; // we don't ever care if the event had listeners, so just return "yes"
    }

    public destroy(): void {
        // Dev note: It's critical that we call stop() during cleanup to ensure that downstream callers
        // are aware of the final clock position before the user triggered an unload.
        // noinspection JSIgnoredPromiseFromCall - not concerned about being called async here
        this.stop();
        this.removeAllListeners();
        this.clock.destroy();
        this.waveformObservable.close();
        if (this.element) {
            this.element.removeEventListener("ended", this.onPlaybackEnd);
            URL.revokeObjectURL(this.element.src);
            this.element.remove();
        }
    }

    public async prepare(): Promise<void> {
        // Don't attempt to decode the media again.
        // AudioContext.decodeAudioData detaches the array buffer `this.buf`
        // meaning it cannot be re-read.
        if (this.state !== PlaybackState.Decoding || this.buf.byteLength === 0) {
            return;
        }

        // Always create an HTMLAudioElement for playback. This enables native
        // variable-speed playback with pitch preservation (preservesPitch).
        // The Blob snapshot is taken before any decodeAudioData call, which
        // would detach the underlying ArrayBuffer.
        this.element = document.createElement("audio") as HTMLAudioElement;
        this.element.addEventListener("ended", this.onPlaybackEnd);
        const deferred = Promise.withResolvers<unknown>();
        this.element.onloadeddata = deferred.resolve;
        this.element.onerror = deferred.reject;
        this.element.src = URL.createObjectURL(new Blob([this.buf]));
        await deferred.promise;

        // For smaller files, also decode the buffer to extract a real waveform.
        // Large files skip this to avoid excessive memory usage.
        if (this.buf.byteLength <= 5 * 1024 * 1024) {
            try {
                // ORIGINAL
                // this.audioBuf = await this.context.decodeAudioData(this.buf);
                // TENTATIVO CON CLONE DEL BUFFER PER EVITARE PROBLEMI DI DETACH (vedi compat.ts)
                this.audioBuf = await this.context.decodeAudioData(cloneArrayBuffer(this.buf));
            } catch (e) {
                logger.error("Error decoding recording:", e);
                logger.warn("Trying to re-encode to WAV instead...");
                try {
                    // Safari doesn't support Opus/Ogg very well.
                    // ORIGINAL
                    // const wav = await decodeOgg(this.buf);
                    // this.audioBuf = await this.context.decodeAudioData(wav);

                    // TENTATIVO CON CLONE DEL BUFFER PER EVITARE PROBLEMI DI DETACH (vedi compat.ts)
                    const wav = await decodeOgg(cloneArrayBuffer(this.buf));
                    this.audioBuf = await this.context.decodeAudioData(cloneArrayBuffer(wav));
                } catch (e) {
                    logger.error("Error decoding recording:", e);
                    // Waveform will fall back to the seed data; playback still works.
                }
            }

            if (this.audioBuf) {
                this.resampledWaveform = await PlaybackEncoder.instance.getPlaybackWaveform(
                    this.audioBuf.getChannelData(0),
                );
            }
        }

        this.waveformObservable.update(this.resampledWaveform);

        this.clock.flagLoadTime();
        this.clock.durationSeconds = this.element.duration;
        this.clock.setElement(this.element);

        this.emit(PlaybackState.Stopped);
    }

    private onPlaybackEnd = async (): Promise<void> => {
        // Suspend the AudioContext in the background – don't let a
        // hanging/rejecting suspend() block the UI state update.
        this.context.suspend().catch((e) => {
            logger.warn("Failed to suspend AudioContext on playback end:", e);
        });
        if (this.element) {
            this.element.currentTime = 0;
        }
        this.emit(PlaybackState.Stopped);
    };

    public async play(): Promise<void> {
        if (this.state === PlaybackState.Stopped) {
            this.makeNewSourceBuffer();
            if (this.element) {
                this.element.currentTime = 0;
            }
        }

        if (this.element) {
            this.element.playbackRate = this._playbackRate;
            this.element.preservesPitch = true;
            await this.element.play();
        }

        // Resume the AudioContext in the background – the HTMLAudioElement
        // drives actual playback, so don't let a hanging/rejecting
        // context.resume() block the clock start and UI state update.
        this.context.resume().catch((e) => {
            logger.warn("Failed to resume AudioContext:", e);
        });
        this.clock.flagStart();
        this.emit(PlaybackState.Playing);
    }

    private makeNewSourceBuffer(): void {
        if (this.source) return; // MediaElementSource is reusable
        if (this.element) {
            this.source = this.context.createMediaElementSource(this.element);
            this.source.connect(this.context.destination);
        }
    }

    public async pause(): Promise<void> {
        if (this.element) {
            this.element.pause();
        }
        // Suspend the AudioContext in the background – the element.pause()
        // already stops audible output, so don't let a hanging/rejecting
        // context.suspend() block the UI state update.
        this.context.suspend().catch((e) => {
            logger.warn("Failed to suspend AudioContext:", e);
        });
        this.emit(PlaybackState.Paused);
    }

    public async stop(): Promise<void> {
        await this.onPlaybackEnd();
        this.clock.flagStop();
    }

    public async toggle(): Promise<void> {
        if (this.isPlaying) await this.pause();
        else await this.play();
    }

    public async skipTo(timeSeconds: number): Promise<void> {
        timeSeconds = clamp(timeSeconds, 0, this.clock.durationSeconds);

        const isPlaying = this.isPlaying;

        if (isPlaying) {
            if (this.element) this.element.pause();
            this.context.suspend().catch((e) => {
                logger.warn("Failed to suspend AudioContext during skip:", e);
            });
        }

        if (this.element) {
            this.element.currentTime = timeSeconds;
        }

        // Force a clock update so the UI refreshes immediately.
        this.clock.syncTo(this.context.currentTime, timeSeconds);

        if (isPlaying) {
            if (this.element) await this.element.play();
            this.context.resume().catch((e) => {
                logger.warn("Failed to resume AudioContext during skip:", e);
            });
        }
    }
}
